/**
 * @module yeymaps/View
 * @description
 * Extended View class with custom zooming utilities for Yeymaps.
 */

import OlView, * as ViewModule from './../node_modules/ol/View.js';
import Feature from './../node_modules/ol/Feature.js';
import Point from './../node_modules/ol/geom/Point.js';

/**
 * Yeymaps custom View class extending OpenLayers View.
 */
class View extends OlView {
  /**
   * @param {import('ol/View').ViewOptions} options - OpenLayers View options.
   */
  constructor(options = {}) {
    super(options);
  }

  /**
   * Smoothly zooms the view to fit an Extent, Feature, or coordinate.
   *
   * @param {Extent|Feature|number[]} target - Target extent, feature, or [x, y] coordinate.
   * @param {Object} [options={}] - Additional fit options.
   * @param {number} [options.minResolution] - Minimum resolution (optional).
   * @param {number[]} [options.padding=[0,0,0,0]] - Padding around the target area.
   * @param {number} [options.duration=500] - Animation duration in milliseconds.
   */
  zoomTo(target, options = {}) {
    const { minResolution, padding = [0, 0, 0, 0], duration = 500 } = options;
    const fitOptions = { minResolution, padding, duration };

    // Handle Extent
    if (target instanceof Array && target.length === 4) {
      const extentCoords = target.coordinates || target;
      if (Array.isArray(extentCoords) && extentCoords.every(Number.isFinite)) {
        this.fit(extentCoords, fitOptions);
      } else {
        console.warn('zoomTo(): Invalid extent coordinates.');
      }
      return;
    }

    // Handle Feature
    if (target instanceof Feature) {
      const geometry = target.getGeometry();
      if (!geometry) {
        console.warn('zoomTo(): Feature has no geometry.');
        return;
      }

      // Prevent overzoom for points
      if (geometry instanceof Point && !fitOptions.minResolution) {
        fitOptions.minResolution = this.getResolutionForScale(1000);
      }

      this.fit(geometry, fitOptions);
      return;
    }

    // Handle coordinate center [x, y]
    if (Array.isArray(target) && target.length === 2 && target.every(Number.isFinite)) {
      this.animate({
        center: target,
        duration,
        zoom: this.getZoom() ?? 10
      });
      return;
    }

    console.warn('zoomTo(): Unsupported target type. Expected Extent, Feature, or coordinate array.');
  }
}

// Copy all named exports as static methods
for (const key in ViewModule) {
    if (key !== "default") {
        View[key] = ViewModule[key];
    }
}

export default View;

// Re-export ALL named exports from OL
export * from './../node_modules/ol/View.js';